import { useSelectedPersonBackgroundCheckData } from '@/ui/hooks/useSelectedPersonBackgroundCheckData';
import { handleErrorDialog } from '@juvare/common-web-ui/DialogHelper';
import { allowSwitchUser, allowUserSendMessages } from '@juvare/contact-management/backend/features/permissions/shared';
import { SlideoutMenuItem as SlideoutMenuItemCM } from '@juvare/contact-management/ui/components/SlideoutMenuButton';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import { ContactManagementConfigContext } from '@juvare/contact-management/ui/providers';
import { useUserClient } from '@juvare/platform-sdk-react/ui/hooks';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { AppContext } from '../Context';
import { useRequestBackgroundCheckDetails } from '../features/background-check/hooks';
import { useCredentialDetailsConfig } from './useCredentialDetailsConfig';
import { useSelectedPerson } from './useSelectedPerson';

export interface SlideoutMenuItem {
    label: string;
    onClick: () => void;
    'data-test'?: string;
}

export type SlideOutMenuItemWithId = SlideoutMenuItem & SlideoutMenuItemCM;

interface Result {
    menuItems: SlideoutMenuItem[];
    handlePersonIdSelect: (personId: string) => void;
    handlePersonIdReset: () => void;
    selectedPersonId?: string;
}

export const useSlideout = (): Result => {
    const userState = useUserState();
    const { switchUserPrev } = React.useContext(AppContext);
    const { notificationConfig } = React.useContext(ContactManagementConfigContext);
    const userClient = useUserClient();
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const { person, handlePersonIdSelect, handlePersonIdReset, selectedPersonId } = useSelectedPerson();
    const { backgroundCheckData } = useSelectedPersonBackgroundCheckData(selectedPersonId);
    const id = searchParams.get('id');
    const prevIdRef = React.useRef<string | null>(id);

    // We only want to reset the selected person when the URL previously contained an `id`
    // query param but no longer does.
    // Missions library never uses `id` in the query string, `id` will always be null.
    // Without this guard, the effect would incorrectly reset selection every time a
    // person is chosen, preventing the slideout from staying open.
    // Using `prevIdRef` ensures we only trigger a reset on an actual "id → null" transition (usage - screenings tables).
    React.useEffect(() => {
        if (prevIdRef.current && !id) {
            handlePersonIdReset();
        }
        prevIdRef.current = id;
    }, [id, handlePersonIdReset]);

    const { getBackgroundCheckMenuItems } = useRequestBackgroundCheckDetails();
    const credentialDetailsConfig = useCredentialDetailsConfig();

    const getMenuItems = (): SlideoutMenuItem[] => {
        if (!person) {
            return [];
        }

        const backgroundCheckMenuItems = backgroundCheckData ? getBackgroundCheckMenuItems(person, backgroundCheckData) : [];
        const credentialMenuItems = credentialDetailsConfig.getMenuItems(person);

        const menuItems: SlideoutMenuItem[] = [...backgroundCheckMenuItems, ...credentialMenuItems];

        if (allowSwitchUser(userState) && ['active', 'inactive'].includes(person.data.systemStatus) && !switchUserPrev) {
            menuItems.push({
                label: t('menuItems.switchToThisAccount'),
                onClick: () => {
                    userClient
                        .switchUser({
                            provider: 'switch',
                            user: person.columns.email,
                        })
                        .then((response) => {
                            window.location.assign(response.loginSuccessUrl);
                        })
                        .catch(handleErrorDialog);
                },
                'data-test': 'menu-item-switch-to-user',
            });
        }
        if (notificationConfig?.onAccountNotificationSend && person.data.systemStatus === 'active' && allowUserSendMessages(userState)) {
            menuItems.push({
                label: t('menuItems.sendNotification'),
                onClick: () => notificationConfig?.onAccountNotificationSend?.([person.id]),
                'data-test': 'menu-item-send-message',
            });
        }

        return menuItems;
    };

    return { menuItems: getMenuItems(), handlePersonIdSelect, handlePersonIdReset, selectedPersonId };
};
